import { useRecordingSetupSelection } from "@/hooks/useRecordingSetupSelection";
import { useCallback, useState } from "react";
import { PrepareRecordingContext } from "./PrepareRecordingContext";
import { MicrophoneStatus } from "@/types/microphoneStatus";
import { useParams } from "react-router-dom";
import usePostRecoverRecordingUUID from "@/hooks/mutations/usePostRecoverRecordingUUID";
import { getUploadURL } from "@/services/getUploadURL";
import { useDeviceKey } from "@/hooks/useDeviceKey";
import { useTranslation } from "react-i18next";
import { formatMsToTime } from "@/utils/formatTime";
import { RecordStatusEnum } from "@/pages/RecordsList/types";
import { RecordingData } from "@/utils/indexedDBUtils";
import { buildRecordingSetupData } from "@/utils/helpers/buildRecordingSetupData";
import { RecordingCreationType } from "@/models/recording";

export interface PrepareRecordingProviderProps {
  children: React.ReactNode;
}

export function PrepareRecordingProvider({ children }: PrepareRecordingProviderProps) {
  const recordingSetupSelection = useRecordingSetupSelection();
  const { agendasList, selectedAgendaId, selectedAttendees, selectedLanguage } = recordingSetupSelection;
  const [selectedAudioType, setSelectedAudioType] = useState<string>("default");
  const [isAudioActive, setIsAudioActive] = useState<boolean>(false);

  const { lang } = useParams();
  const { t } = useTranslation();
  const deviceKey = useDeviceKey();

  const { mutateAsync: createNewRecording } = usePostRecoverRecordingUUID();

  const onMicrophoneStatusChange = useCallback((status: MicrophoneStatus) => {
    setIsAudioActive(status === "active");
  }, []);

  const createNewRecordingHandler = useCallback(async () => {
    const setupData = await buildRecordingSetupData({
      selectedAgendaId,
      agendasList,
      selectedAttendees,
      selectedLanguage,
      lang,
      deviceKey,
      createNewRecording,
      t,
      recordingType: RecordingCreationType.LIVE_RECORDING,
    });

    const uploadUrl = await getUploadURL(setupData.recordingUUID, deviceKey);
    const finalUploadURLWithFileExtension = uploadUrl.split(setupData.recordingUUID).join(`${setupData.recordingUUID}.webm`);

    const recordingData: RecordingData = {
      id: `recording-${setupData.recordingUUID}-${Date.now()}`,
      meetingId: setupData.meetingId,
      meetingName: setupData.meetingName,
      meetingDateTime: setupData.meetingDateTime,
      meetingStartDate: setupData.meetingStartDate,
      meetingEndDate: setupData.meetingEndDate,
      guestsList: setupData.guestsList,
      guestsListInitials: setupData.guestsListInitials,
      guestsListFullName: setupData.guestsListFullName,
      selectedLanguage: setupData.language,
      audioInput: selectedAudioType,
      recordingUUID: setupData.recordingUUID || "",
      uploadURL: finalUploadURLWithFileExtension,
      subjects: setupData.subjects,
      duration: formatMsToTime(0),
      durationTimestamp: 0,
      timestamp: Date.now(),
      status: RecordStatusEnum.NONE,
      subjectIndex: 0,
      recordingType: RecordingCreationType.LIVE_RECORDING,
    };

    return recordingData;
  }, [agendasList, createNewRecording, deviceKey, lang, selectedAgendaId, selectedAttendees, selectedAudioType, selectedLanguage, t]);

  return (
    <PrepareRecordingContext.Provider
      value={{
        ...recordingSetupSelection,
        selectedAudioType,
        setSelectedAudioType,
        isAudioActive,
        onMicrophoneStatusChange,
        createNewRecordingHandler,
      }}
    >
      {children}
    </PrepareRecordingContext.Provider>
  );
}
